interface Html2CanvasOptions {
  scale?: number;
  useCORS?: boolean;
  allowTaint?: boolean;
  backgroundColor?: string;
  logging?: boolean;
  width?: number;
  height?: number;
  x?: number;
  y?: number;
  ignoreElements?: (element: Element) => boolean;
  scrollX?: number;
  scrollY?: number;
  windowWidth?: number;
  windowHeight?: number;
  foreignObjectRendering?: boolean;
  onclone?: (document: Document) => void;
}

export type ImageFormat = "png" | "jpeg" | "jpg" | "webp";

export interface PropertyCaptureOptions {
  format?: ImageFormat | "pdf";
  quality?: number;
  scale?: number;
  fileName?: string;
  backgroundColor?: string;
  excludeSelector?: string;
}

export interface PropertyCaptureResult {
  blob: Blob;
  dataUrl: string;
  fileName: string;
  mimeType: string;
  file: File;
}

export interface PropertyData {
  uuid: string;
  title: string;
  auctionId: string;
  bankName: string;
  reservePrice: string;
  emd: string;
  possession: string;
  area: string;
  propertyType: string;
  location: string;
  image: string;
  sellerName?: string;
  sellerType?: string;
  auctionDetails?: Array<{
    label: string;
    value: string;
  }>;
}

export type { PropertyData as SharePropertyData };

const loadHtml2Canvas = async (): Promise<
  typeof import("html2canvas")["default"]
> => {
  const html2canvasModule = await import("html2canvas");
  return html2canvasModule.default;
};

const loadJsPDF = async (): Promise<typeof import("jspdf")["jsPDF"]> => {
  const jsPDFModule = await import("jspdf");
  return jsPDFModule.jsPDF;
};

const getMimeType = (format: ImageFormat | "pdf"): string => {
  const mimeTypes: Record<string, string> = {
    png: "image/png",
    jpeg: "image/jpeg",
    jpg: "image/jpeg",
    webp: "image/webp",
    pdf: "application/pdf",
  };
  return mimeTypes[format] || "image/png";
};

const getFileExtension = (format: ImageFormat | "pdf"): string => {
  if (format === "jpg") return "jpeg";
  return format;
};

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality: number
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to create blob from canvas"));
        }
      },
      mimeType,
      quality
    );
  });
};

const preloadImages = async (element: HTMLElement): Promise<void> => {
  const images = element.querySelectorAll("img");
  const loadPromises = Array.from(images).map((img) => {
    return new Promise<void>((resolve) => {
      if (img.complete && img.naturalWidth > 0) {
        resolve();
        return;
      }

      if (!img.crossOrigin) {
        img.crossOrigin = "anonymous";
      }

      const handleLoad = () => {
        resolve();
      };

      const handleError = () => {
        console.warn(`Failed to load image: ${img.src}`);
        resolve();
      };

      img.addEventListener("load", handleLoad, { once: true });
      img.addEventListener("error", handleError, { once: true });

      if (img.complete && img.naturalWidth === 0) {
        setTimeout(resolve, 100);
      }
    });
  });

  // Wait for all images with a timeout
  await Promise.race([
    Promise.all(loadPromises),
    new Promise((resolve) => setTimeout(resolve, 5000)), // 5 second timeout
  ]);
};

export const capturePropertyAsImage = async (
  element: HTMLElement,
  options: PropertyCaptureOptions = {}
): Promise<PropertyCaptureResult> => {
  const {
    format = "png",
    quality = 0.92,
    scale = 2,
    fileName = "property-card",
    backgroundColor = "#ffffff",
    excludeSelector = ".no-capture",
  } = options;

  await preloadImages(element);

  const html2canvas = await loadHtml2Canvas();

  // Configure html2canvas options with CORS handling
  const canvasOptions: Html2CanvasOptions = {
    scale,
    useCORS: true, // Enable cross-origin images
    allowTaint: true, // Allow tainted canvas for better CORS handling
    backgroundColor,
    logging: false, // Disable logging in production
    // Disable foreignObject for more reliable text rendering
    foreignObjectRendering: false,
    // Ensure proper scroll position
    scrollX: 0,
    scrollY: -window.scrollY, // Account for scroll position
    // Window dimensions
    windowWidth: document.documentElement.offsetWidth,
    windowHeight: document.documentElement.offsetHeight,
    // Clone callback to fix positioning issues
    onclone: (clonedDoc) => {
      // Fix flexbox and positioning issues in cloned document
      const allElements = clonedDoc.querySelectorAll("*");
      allElements.forEach((el) => {
        const htmlEl = el as HTMLElement;
        const computedStyle = window.getComputedStyle(el as Element);

        // Fix absolute positioned elements
        if (computedStyle.position === "absolute") {
          htmlEl.style.position = "absolute";
          // Ensure proper box-sizing
          htmlEl.style.boxSizing = "border-box";
        }

        // Fix flex items
        if (computedStyle.display === "flex") {
          htmlEl.style.display = "flex";
        }

        // Remove any transforms that might affect positioning
        if (computedStyle.transform && computedStyle.transform !== "none") {
          htmlEl.style.transform = "none";
        }
      });
    },
    ignoreElements: (el) => {
      // Exclude elements matching selector (like share buttons)
      if (excludeSelector && el.matches && el.matches(excludeSelector)) {
        return true;
      }
      // Exclude broken images
      if (el.tagName === "IMG" && (el as HTMLImageElement).naturalWidth === 0) {
        return true;
      }
      return false;
    },
  };

  try {
    // Capture element
    const canvas = await html2canvas(element, canvasOptions);

    const mimeType = getMimeType(format as ImageFormat);
    const extension = getFileExtension(format as ImageFormat);
    const fullFileName = `${fileName}.${extension}`;

    // Convert to blob
    const blob = await canvasToBlob(canvas, mimeType, quality);
    const dataUrl = canvas.toDataURL(mimeType, quality);

    // Create File object for sharing
    const file = new File([blob], fullFileName, { type: mimeType });

    return {
      blob,
      dataUrl,
      fileName: fullFileName,
      mimeType,
      file,
    };
  } catch (error) {
    console.error("Capture failed, retrying without CORS images:", error);

    // Retry with CORS disabled - will exclude external images
    const fallbackOptions: Html2CanvasOptions = {
      ...canvasOptions,
      useCORS: false,
      allowTaint: false,
      ignoreElements: (el) => {
        // Exclude all images in fallback mode
        if (el.tagName === "IMG") {
          return true;
        }
        if (excludeSelector && el.matches && el.matches(excludeSelector)) {
          return true;
        }
        return false;
      },
    };

    const canvas = await html2canvas(element, fallbackOptions);

    const mimeType = getMimeType(format as ImageFormat);
    const extension = getFileExtension(format as ImageFormat);
    const fullFileName = `${fileName}.${extension}`;

    const blob = await canvasToBlob(canvas, mimeType, quality);
    const dataUrl = canvas.toDataURL(mimeType, quality);
    const file = new File([blob], fullFileName, { type: mimeType });

    return {
      blob,
      dataUrl,
      fileName: fullFileName,
      mimeType,
      file,
    };
  }
};

export const capturePropertyAsPDF = async (
  element: HTMLElement,
  options: PropertyCaptureOptions = {}
): Promise<PropertyCaptureResult> => {
  const {
    scale = 2,
    fileName = "property-card",
    backgroundColor = "#ffffff",
    excludeSelector = ".no-capture",
  } = options;

  // Preload images before capture
  await preloadImages(element);

  // First capture as image
  const html2canvas = await loadHtml2Canvas();
  const jsPDF = await loadJsPDF();

  const canvasOptions: Html2CanvasOptions = {
    scale,
    useCORS: true,
    allowTaint: true,
    backgroundColor,
    logging: false,
    foreignObjectRendering: false,
    scrollX: 0,
    scrollY: -window.scrollY,
    windowWidth: document.documentElement.offsetWidth,
    windowHeight: document.documentElement.offsetHeight,
    onclone: (clonedDoc) => {
      const allElements = clonedDoc.querySelectorAll("*");
      allElements.forEach((el) => {
        const htmlEl = el as HTMLElement;
        const computedStyle = window.getComputedStyle(el as Element);
        if (computedStyle.position === "absolute") {
          htmlEl.style.position = "absolute";
          htmlEl.style.boxSizing = "border-box";
        }
        if (computedStyle.display === "flex") {
          htmlEl.style.display = "flex";
        }
        if (computedStyle.transform && computedStyle.transform !== "none") {
          htmlEl.style.transform = "none";
        }
      });
    },
    ignoreElements: (el) => {
      if (excludeSelector && el.matches && el.matches(excludeSelector)) {
        return true;
      }
      if (el.tagName === "IMG" && (el as HTMLImageElement).naturalWidth === 0) {
        return true;
      }
      return false;
    },
  };

  try {
    const canvas = await html2canvas(element, canvasOptions);
    const imgData = canvas.toDataURL("image/jpeg", 0.95);

    // A4 dimensions in mm (portrait)
    const a4Width = 210;
    const a4Height = 297;
    const margin = 10; // 10mm margin
    const contentWidth = a4Width - margin * 2;
    const contentHeight = a4Height - margin * 2;

    // Calculate how many pages needed
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const aspectRatio = imgWidth / imgHeight;

    // Calculate scaled dimensions to fit content width
    const scaledHeight = contentWidth / aspectRatio;

    // Create PDF with A4 format
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    // Check if content fits in single page
    const totalContentHeight = scaledHeight;
    const pageHeight = contentHeight;

    if (totalContentHeight <= pageHeight) {
      // Content fits in single page - no scaling needed
      pdf.addImage(imgData, "JPEG", margin, margin, contentWidth, scaledHeight);
    } else {
      // Content exceeds single page - scale to fit
      const scaleFactor = pageHeight / totalContentHeight;
      const finalWidth = contentWidth * scaleFactor;
      const finalHeight = pageHeight;

      // Center the content horizontally
      const xOffset = margin + (contentWidth - finalWidth) / 2;

      pdf.addImage(imgData, "JPEG", xOffset, margin, finalWidth, finalHeight);
    }

    // Generate blob
    const pdfBlob = pdf.output("blob");
    const dataUrl = pdf.output("datauristring");
    const fullFileName = `${fileName}.pdf`;
    const file = new File([pdfBlob], fullFileName, { type: "application/pdf" });

    return {
      blob: pdfBlob,
      dataUrl,
      fileName: fullFileName,
      mimeType: "application/pdf",
      file,
    };
  } catch (error) {
    console.error("PDF capture failed, retrying without CORS images:", error);

    // Retry without CORS
    const fallbackOptions: Html2CanvasOptions = {
      ...canvasOptions,
      useCORS: false,
      allowTaint: false,
      ignoreElements: (el) => {
        if (el.tagName === "IMG") {
          return true;
        }
        if (excludeSelector && el.matches && el.matches(excludeSelector)) {
          return true;
        }
        return false;
      },
    };

    const canvas = await html2canvas(element, fallbackOptions);
    const imgData = canvas.toDataURL("image/jpeg", 0.95);

    // A4 dimensions in mm (portrait)
    const a4Width = 210;
    const a4Height = 297;
    const margin = 10;
    const contentWidth = a4Width - margin * 2;
    const contentHeight = a4Height - margin * 2;

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const aspectRatio = imgWidth / imgHeight;

    const scaledHeight = contentWidth / aspectRatio;
    const pageHeight = contentHeight;

    if (scaledHeight <= pageHeight) {
      pdf.addImage(imgData, "JPEG", margin, margin, contentWidth, scaledHeight);
    } else {
      const scaleFactor = pageHeight / scaledHeight;
      const finalWidth = contentWidth * scaleFactor;
      const finalHeight = pageHeight;
      const xOffset = margin + (contentWidth - finalWidth) / 2;

      pdf.addImage(imgData, "JPEG", xOffset, margin, finalWidth, finalHeight);
    }

    const pdfBlob = pdf.output("blob");
    const dataUrl = pdf.output("datauristring");
    const fullFileName = `${fileName}.pdf`;
    const file = new File([pdfBlob], fullFileName, { type: "application/pdf" });

    return {
      blob: pdfBlob,
      dataUrl,
      fileName: fullFileName,
      mimeType: "application/pdf",
      file,
    };
  }
};

/**
 * Main capture function that handles both image and PDF formats
 *
 * @param element - The DOM element to capture
 * @param options - Capture options including format
 * @returns Promise<PropertyCaptureResult>
 */
export const capturePropertyElement = async (
  element: HTMLElement,
  options: PropertyCaptureOptions = {}
): Promise<PropertyCaptureResult> => {
  const { format = "png" } = options;

  if (format === "pdf") {
    return capturePropertyAsPDF(element, options);
  }

  return capturePropertyAsImage(element, options);
};

/**
 * Download captured property content
 *
 * @param result - The capture result
 */
export const downloadPropertyCapture = (
  result: PropertyCaptureResult
): void => {
  const link = document.createElement("a");
  link.href = result.dataUrl;
  link.download = result.fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
